import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@mui/material';

import { Bar } from '@visx/shape';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { AxisBottom } from '@visx/axis';
import { scaleLinear } from '@visx/scale';

import {
  getParams,
  getMonth,
  thousandFormat2,
} from '../../../../Utils/Functions';
import {
  positiveActiveColor,
  positiveDisableColor,
  negativeActiveColor,
  negativeDisableColor,
  barThinHeight,
} from '../../../../Assets/js/constant';

let tooltipTimeout;

const BottomChart = ({
  detailData,
  selectedYears,
  selectedTopItems,
  selectedMiddleItems,
  maxAccountSubTypeLength,
  maxAccountNumberLengths,
  maxAccountNameLengths,
  handleFilter,
}) => {
  const {
    showTooltip,
    hideTooltip,
    tooltipOpen,
    tooltipTop,
    tooltipLeft,
    tooltipData,
  } = useTooltip();

  const [state, setState] = useState({
    resize: false,
    hoverBar: null,
    selectedBars: [],
    selectedBarObjs: [],
    data: [],
    chartDepth: 0,
    pageWidth: window.innerWidth,
    pageHeight: window.innerHeight,
    pageOffset: 0,
  });

  const updateState = useCallback((updates) => {
    setState(prevState => ({ ...prevState, ...updates }));
  }, []);

  const onResize = useCallback(() => {
    updateState({
      resize: !state.resize,
      pageWidth: window.innerWidth,
      pageHeight: window.innerHeight,
    });
  }, [state.resize, updateState]);

  const prepareData = useCallback(() => {
    let dictData = {};
    let data = [];

    let selectedTopYears = [];

    if (selectedTopItems.length === 0 /*|| selectedMiddleItems.length === 0*/) {
      selectedYears.forEach((item) => {
        selectedTopYears.push(item);
      });
    } else {
      selectedTopItems.forEach((item) => {
        if (selectedTopYears.indexOf(item.year) === -1)
          selectedTopYears.push(item.year);
      });
    }

    let isInFilter = false;
    if (detailData) {
      detailData.forEach((item) => {
        let month = getMonth(item.Date);
        isInFilter = false;
        if (selectedTopItems.length === 0) {
          if (
            selectedMiddleItems.length === 0 ||
            selectedMiddleItems.indexOf(item.AccountType) > -1
          ) {
            isInFilter = true;
          }
        } else {
          selectedTopItems.forEach((topitem) => {
            if (
              topitem.year === item.FY &&
              (topitem.month === month || topitem.month === undefined)
            ) {
              if (
                selectedMiddleItems.length === 0 ||
                selectedMiddleItems.indexOf(item.AccountType) > -1
              ) {
                isInFilter = true;
              }
            }
          });
        }

        if (isInFilter === true) {
          if (dictData[item.AccountSubType] === undefined) {
            dictData[item.AccountSubType] = {};
            dictData[item.AccountSubType]['ActualExpenses'] = 0;
            dictData[item.AccountSubType]['ForecastExpenses'] = 0;
            dictData[item.AccountSubType]['AccountType'] = item.AccountType;
            dictData[item.AccountSubType]['AccountSubType'] =
              item.AccountSubType;
            dictData[item.AccountSubType]['Account'] = {};
          }
          dictData[item.AccountSubType]['ActualExpenses'] += parseFloat(
            item.ActualExpenses,
          );
          dictData[item.AccountSubType]['ForecastExpenses'] += parseFloat(
            item.ForecastExpenses,
          );

          if (
            dictData[item.AccountSubType]['Account'][item.AccountName] ===
            undefined
          ) {
            dictData[item.AccountSubType]['Account'][item.AccountName] = {};
            dictData[item.AccountSubType]['Account'][item.AccountName][
              'AccountNumber'
            ] = item.AccountNumber;
            dictData[item.AccountSubType]['Account'][item.AccountName][
              'ActualExpenses'
            ] = 0;
            dictData[item.AccountSubType]['Account'][item.AccountName][
              'ForecastExpenses'
            ] = 0;
            dictData[item.AccountSubType]['Account'][item.AccountName][
              'AccountName'
            ] = item.AccountName;
            dictData[item.AccountSubType]['Account'][item.AccountName][
              'AccountSubType'
            ] = item.AccountSubType;
            dictData[item.AccountSubType]['Account'][item.AccountName][
              'AccountType'
            ] = item.AccountType;
          }
          dictData[item.AccountSubType]['Account'][item.AccountName][
            'ActualExpenses'
          ] += parseFloat(item.ActualExpenses);
          dictData[item.AccountSubType]['Account'][item.AccountName][
            'ForecastExpenses'
          ] += parseFloat(item.ForecastExpenses);
        }
      });
    }

    // data = []; // error test
    Object.keys(dictData).forEach((key) => {
      let item = {};
      let subitem = [];
      Object.keys(dictData[key].Account).forEach((subkey) => {
        let _item = {};
        _item['AccountName'] = dictData[key]['Account'][subkey].AccountName;
        _item['AccountNumber'] = dictData[key]['Account'][subkey].AccountNumber;
        _item['ActualExpenses'] =
          dictData[key]['Account'][subkey].ActualExpenses;
        _item['ForecastExpenses'] =
          dictData[key]['Account'][subkey].ForecastExpenses;
        _item['AccountType'] = dictData[key]['Account'][subkey].AccountType;
        _item['AccountSubType'] =
          dictData[key]['Account'][subkey].AccountSubType;
        subitem.push(_item);
      });
      item['AccountSubType'] = dictData[key].AccountSubType;
      item['ActualExpenses'] = dictData[key].ActualExpenses;
      item['ForecastExpenses'] = dictData[key].ForecastExpenses;
      item['AccountType'] = dictData[key].AccountType;
      item['Account'] = subitem;
      data.push(item);
    });

    updateState({
      data: data,
    });
  }, [detailData, selectedTopItems, selectedMiddleItems, selectedYears, updateState]);

  const getColor = useCallback((bar) => {
    const { selectedBarObjs } = state;

    if (bar.ActualExpenses === undefined || bar.ForecastExpenses === undefined)
      return positiveActiveColor;

    let activeColor; //= "#27aae1";
    let disableColor; // = "#1c75bc";
    if (bar.ActualExpenses <= bar.ForecastExpenses) {
      activeColor = positiveActiveColor;
      disableColor = positiveDisableColor;
    } else {
      activeColor = negativeActiveColor;
      disableColor = negativeDisableColor;
    }

    let index = -1;
    if (selectedBarObjs && selectedBarObjs.length !== 0) {
      selectedBarObjs.forEach((item) => {
        if (item.AccountName === bar.AccountName) {
          index++;
        }
      });
    } else {
      return activeColor;
    }

    if (index >= 0) return activeColor;

    return disableColor;
  }, [state.selectedBarObjs]);

  const handleBar = useCallback((event, bar) => {
    const { selectedBars, selectedBarObjs } = state;

    let _selectedBars, _selectedBarObjs;

    if (event.shiftKey) {
      _selectedBars = selectedBars.slice();
      _selectedBarObjs = selectedBarObjs.slice();

      let index = NaN;
      for (let i = 0; i < selectedBars.length; i++) {
        if (selectedBars[i] === event.target) {
          index = i;
          break;
        }
      }

      if (isNaN(index)) {
        event.target.classList.add('barActive');
        _selectedBars.push(event.target);
        _selectedBarObjs.push(bar);
      } else {
        event.target.classList.remove('barActive');
        _selectedBars.splice(index, 1);
        _selectedBarObjs.splice(index, 1);
      }
    } else {
      let exist = false;

      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
        if (selectedBar === event.target) exist = true;
      });

      if (exist && selectedBars.length === 1) {
        _selectedBars = [];
        _selectedBarObjs = [];
      } else {
        _selectedBars = [event.target];
        _selectedBarObjs = [bar];
        event.target.classList.add('barActive');
      }
    }

    updateState({
      selectedBars: _selectedBars,
      selectedBarObjs: _selectedBarObjs,
    });
  }, [state.selectedBars, state.selectedBarObjs, updateState]);

  const deSelectAll = useCallback(() => {
    const { hoverBar, selectedBars } = state;

    if (hoverBar) hoverBar.classList.remove('barHover');

    selectedBars.forEach((selectedBar) => {
      selectedBar.classList.remove('barActive');
    });

    updateState({
      hoverBar: null,
      selectedBars: [],
      selectedBarObjs: [],
    });

    handleFilter({
      selectedTopItems: [],
    });
  }, [state, updateState, handleFilter]);

  const showTooltipHandler = useCallback((event, xScale, bar, isBar = true, index) => {
    if (tooltipTimeout) clearTimeout(tooltipTimeout);
    let data = {},
      top,
      left;
    top = event.clientY - 50;
    left =
      window.innerWidth / 2 < event.clientX
        ? event.clientX - 40
        : event.clientX + 250;

    if (isBar) {
      const { hoverBar } = state;
      if (hoverBar) hoverBar.classList.remove('barHover');
      event.target.classList.add('barHover');
      updateState({ hoverBar: event.target });
      data = bar;
    } else {
      data['forecast'] = bar;
    }
    data['isBar'] = isBar;

    showTooltip({
      tooltipData: data,
      tooltipTop: top,
      tooltipLeft: left,
    });
  }, [state.hoverBar, updateState, showTooltip]);

  const handlePos = useCallback((event) => {
    const { pageOffset } = state;
    const _pos =
      event.currentTarget.getBoundingClientRect().top + window.scrollY;
    if (pageOffset !== _pos) {
      updateState({ pageOffset: _pos });
    }
  }, [state.pageOffset, updateState]);

  const hideTooltipHandler = useCallback(() => {
    const { hoverBar } = state;

    tooltipTimeout = setTimeout(() => {
      if (hoverBar) hoverBar.classList.remove('barHover');
      updateState({ hoverBar: null });

      hideTooltip();
    }, 300);
  }, [state.hoverBar, updateState, hideTooltip]);

  // Component mount effect
  useEffect(() => {
    onResize();
    window.addEventListener('resize', onResize);
    prepareData();

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // Component update effect for data dependencies
  useEffect(() => {
    prepareData();
  }, [detailData, selectedTopItems, selectedMiddleItems]);

  let { data, chartDepth } = state;

  const width = window.innerWidth - 60;

  const height =
    state.pageWidth <= 1280 && state.pageWidth >= 1270
      ? data.length > 7 ? 23 * 7 + 5 : 23 * data.length + 5
      : data.length > 8 ? Math.floor((window.innerHeight - 721) / 23) * 23 + 5 : 23 * data.length + 3;
  const margin = {
    top: 10,
    right: 0,
    bottom: 20,
    left: 15,
  };

  const { pageYOffset, xMax } = getParams(window, width, height, margin);

  let minData = 0,
    maxData = 0;

  data.forEach((item) => {
    if (chartDepth === 0) {
      maxData = Math.max(maxData, item.ActualExpenses, item.ForecastExpenses);
      minData = Math.min(minData, item.ActualExpenses, item.ForecastExpenses);
    } else {
      item.Account.forEach((subitem) => {
        maxData = Math.max(
          maxData,
          subitem.ActualExpenses,
          subitem.ForecastExpenses,
        );
        minData = Math.min(
          minData,
          subitem.ActualExpenses,
          subitem.ForecastExpenses,
        );
      });
    }
  });

  data.sort((a, b) => b.ActualExpenses - a.ActualExpenses);

  let axisWidth = 0;
  axisWidth = xMax - maxAccountSubTypeLength * 6.9 + 20;
  if (chartDepth === 1)
    axisWidth =
      xMax - maxAccountSubTypeLength * 6.9 - maxAccountNumberLengths * 9 + 20;
  if (chartDepth === 2)
    axisWidth =
      xMax -
      (maxAccountSubTypeLength + maxAccountNameLengths) * 6.6 -
      maxAccountNumberLengths * 9 +
      20;

  const xScale = scaleLinear({
    domain: [minData * 1.2, maxData * 1.2],
    range: [0, axisWidth],
    nice: true,
  });

  return (
    <div
      id="expense-bottom-chart"
      onMouseMove={(event) => handlePos(event)}
      onTouchMove={(event) => handlePos(event)}
    >
      <div className="subtitle mb-10">Detail for Selected Expenses</div>

      <div
        style={{
          overflowX: 'hidden',
          height: `${height}px`,
          paddingLeft: 1,
          borderTop: 'solid 2px #d7d7d7',
          borderLeft: 'solid 2px #d7d7d7',
          borderRight: 'solid 2px #d7d7d7',
          fontSize: 12,
          cursor: 'pointer',
        }}
      >
        {data.map((item, i) => {
          const yMax = barThinHeight - 2;
          item.Account.sort((a, b) => a.AccountNumber - b.AccountNumber);
          return (
            <div style={{ display: 'flex' }} key={i}>
              <div
                style={{
                  padding: '3px 0 0 3px',
                  height: 23,
                  width: maxAccountSubTypeLength * 6.9,
                }}
                {...{
                  onClick: (event) => {
                    let newChartDepth = chartDepth;
                    newChartDepth++;
                    if (newChartDepth > 2) newChartDepth = 0;

                    updateState({ chartDepth: newChartDepth });
                  },
                }}
              >
                {item.AccountSubType}
              </div>
              <div>
                {chartDepth !== 0 ? (
                  item.Account.map((subaccount, key) => {
                    let barWidth =
                      xScale(subaccount.ActualExpenses) - xScale(0);
                    let barHeight = 18;
                    let barX = xScale(0);
                    let barY = 2.5;
                    if (subaccount.ActualExpenses < 0) {
                      barX = xScale(subaccount.ActualExpenses);
                      barWidth =
                        xScale(0) - xScale(subaccount.ActualExpenses);
                    }
                    return (
                      <div className="flex grayHover" key={key}>
                        <Grid container>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              height: 23,
                              padding: '3px 0 0 3px',
                              width: maxAccountNumberLengths * 9,
                            }}
                            {...{
                              onClick: (event) => {
                                let newChartDepth = chartDepth;
                                newChartDepth++;
                                if (newChartDepth > 2) newChartDepth = 0;

                                updateState({
                                  chartDepth: newChartDepth,
                                });
                              },
                            }}
                          >
                            {subaccount.AccountNumber}
                          </div>
                          {chartDepth !== 1 ? (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                height: 23,
                                padding: '3px 0 0 3px',
                                width: maxAccountNameLengths * 6.6,
                              }}
                            >
                              {subaccount.AccountName}
                            </div>
                          ) : null}
                          <div style={{ height: 23 }}>
                            <svg height={yMax} width={axisWidth}>
                              <rect
                                x={0}
                                y={0}
                                width={1.5}
                                height={yMax}
                                fill="darkgrey"
                              />
                              <Bar
                                x={barX}
                                y={barY}
                                width={barWidth}
                                height={barHeight}
                                fill={getColor(subaccount)}
                                onClick={(event) =>
                                  handleBar(event, subaccount)
                                }
                                onMouseLeave={(event) => hideTooltipHandler()}
                                onMouseMove={(event) =>
                                  showTooltipHandler(
                                    event,
                                    xScale,
                                    subaccount,
                                    true,
                                    i,
                                  )
                                }
                                onTouchEnd={(event) => hideTooltipHandler()}
                                onTouchMove={(event) =>
                                  showTooltipHandler(
                                    event,
                                    xScale,
                                    subaccount,
                                    true,
                                    i,
                                  )
                                }
                              ></Bar>
                              <Bar
                                x={xScale(0.5)}
                                y={0}
                                width={1}
                                height={23}
                                fill="#999999"
                                stroke="#999999"
                                strokeWidth={0.5}
                              />
                              <Bar
                                x={xScale(subaccount.ForecastExpenses)}
                                y={barY}
                                width={1}
                                height={barHeight}
                                fill="black"
                                stroke="black"
                                strokeWidth={0.5}
                                onMouseLeave={(event) => hideTooltipHandler()}
                                onMouseMove={(event) =>
                                  showTooltipHandler(
                                    event,
                                    xScale,
                                    subaccount,
                                    false,
                                    i,
                                  )
                                }
                                onTouchEnd={(event) => hideTooltipHandler()}
                                onTouchMove={(event) =>
                                  showTooltipHandler(
                                    event,
                                    xScale,
                                    subaccount,
                                    false,
                                    i,
                                  )
                                }
                              />
                            </svg>
                          </div>
                        </Grid>
                      </div>
                    );
                  })
                ) : (
                  <div className="flex grayHover">
                    {(() => {
                      let barWidth = xScale(item.ActualExpenses) - xScale(0);
                      let barHeight = 18;
                      let barX = xScale(0);
                      let barY = 2.5;
                      if (item.ActualExpenses < 0) {
                        barX = xScale(item.ActualExpenses);
                        barWidth = xScale(0) - xScale(item.ActualExpenses);
                      }

                      return (
                        <div
                          style={{
                            position: 'relative',
                            margin: 0,
                            padding: 0,
                            height: 23,
                          }}
                        >
                          <svg height={yMax} width={axisWidth}>
                            <rect
                              x={0}
                              y={0}
                              width={1.5}
                              height={yMax}
                              fill="darkgrey"
                            />                              
                            <Bar
                              x={barX}
                              y={barY}
                              width={barWidth}
                              height={barHeight}
                              fill={getColor(item)}
                              onClick={(event) =>
                                handleBar(event, item)
                              }
                              onMouseLeave={(event) => hideTooltipHandler()}
                              onMouseMove={(event) =>
                                showTooltipHandler(
                                  event,
                                  xScale,
                                  item,
                                  true,
                                  i,
                                )
                              }
                              onTouchEnd={(event) => hideTooltipHandler()}
                              onTouchMove={(event) =>
                                showTooltipHandler(
                                  event,
                                  xScale,
                                  item,
                                  true,
                                  i,
                                )
                              }
                            ></Bar>
                            <Bar
                              x={xScale(0.5)}
                              y={0}
                              width={1}
                              height={23}
                              fill="#999999"
                              stroke="#999999"
                              strokeWidth={0.5}
                            />
                            <Bar
                              x={xScale(item.ForecastExpenses)}
                              y={barY}
                              width={1}
                              height={barHeight}
                              fill="black"
                              stroke="black"
                              strokeWidth={0.5}
                              onMouseLeave={(event) => hideTooltipHandler()}
                              onMouseMove={(event) =>
                                showTooltipHandler(
                                  event,
                                  xScale,
                                  item,
                                  false,
                                  i,
                                )
                              }
                              onTouchEnd={(event) => hideTooltipHandler()}
                              onTouchMove={(event) =>
                                showTooltipHandler(
                                  event,
                                  xScale,
                                  item,
                                  false,
                                  i,
                                )
                              }
                            />
                          </svg>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {data.length > 0 && (
        <div
          onClick={deSelectAll}
          style={{
            borderTop: 'solid 2px #d7d7d7',
            padding: 0,
            fontSize: 12,
          }}
        >
          <svg width={window.innerWidth - 16} height={barThinHeight}>
            <AxisBottom
              scale={xScale}
              left={maxAccountSubTypeLength * 6.9 + 3.5}
              hideAxisLine={true}
              stroke="black"
              tickStroke="black"
              tickFormat={(value, index) => value / 1000}
              tickLabelProps={(value, index) => ({
                fill: 'black',
                fontSize: 11,
                textAnchor: 'middle',
                dy: '0.2em',
              })}
              tickComponent={({ formattedValue, ...tickProps }) => (
                <text {...tickProps}>
                  $
                  {Math.abs(formattedValue) < 1000
                    ? `${formattedValue}K`
                    : `${formattedValue / 1000}M`}
                </text>
              )}
            />
          </svg>
        </div>
      )}

      {tooltipOpen && (
        <TooltipWithBounds top={tooltipTop + pageYOffset} left={tooltipLeft}>
          {tooltipData.isBar ? (
            <div className="color-black" style={{ width: 220 }}>
              <div className="pdv-10">
                <strong>
                  {tooltipData.AccountType}:&nbsp;
                  {tooltipData.AccountSubType}
                </strong>
              </div>
              <div className="flex">
                Actual:&nbsp;&nbsp;
                <strong>
                  {' '}
                  {thousandFormat2(tooltipData.ActualExpenses)}
                </strong>
              </div>
              <div className="flex">
                Forecast:&nbsp;&nbsp;
                <strong>
                  {' '}
                  {thousandFormat2(tooltipData.ForecastExpenses)}
                </strong>
              </div>
            </div>
          ) : (
            <div className="color-black" style={{ width: 200 }}>
              <div className="pdv-10">
                Forecast :&nbsp;&nbsp;
                <strong>
                  {thousandFormat2(tooltipData.forecast.ForecastExpenses)}
                </strong>
              </div>
            </div>
          )}
        </TooltipWithBounds>
      )}
    </div>
  );
};

BottomChart.propTypes = {
  detailData: PropTypes.array.isRequired,
  selectedYears: PropTypes.array.isRequired,
  selectedTopItems: PropTypes.array.isRequired,
  selectedMiddleItems: PropTypes.array.isRequired,
  maxAccountSubTypeLength: PropTypes.number.isRequired,
  maxAccountNumberLengths: PropTypes.number.isRequired,
  maxAccountNameLengths: PropTypes.number.isRequired,
  handleFilter: PropTypes.func.isRequired,
};

export default BottomChart;
